import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Badge, Spinner, Input,
} from "reactstrap";
import CountUp from "react-countup";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── ID de la catégorie "liens" (Internet & Connectivité) ── */
const LIENS_CATEGORY_ID = 7;

/* ── interfaces ── */
interface ServiceItem {
    contrat: { ref: string };
    service: {
        label: string;
        ref?: string;
        ref_ext?: string;
        description?: string;
        statut_service: string;
        date_ouverture?: string;
        date_ouverture_prevue?: string;
        qty?: number | string;
        categorie_id?: number | string;
        categorie_label?: string;
        extrafields?: Record<string, any>;
    };
}

/* ── helpers ── */
const formatDate = (raw?: string) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "—";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/* Normalisation : la catégorie "liens" peut arriver sous différents libellés */
const isLienCategory = (s: ServiceItem["service"]) => {
    if (s.categorie_id !== undefined && s.categorie_id !== null) {
        return Number(s.categorie_id) === LIENS_CATEGORY_ID;
    }
    return (s.categorie_label || "").toLowerCase().trim() === "liens";
};

/* Récupère un extrafield "intéressant" à afficher (IP, débit, type d'accès, etc.) */
const getExtraInfo = (s: ServiceItem["service"]): { label: string; value: string }[] => {
    const ef = s.extrafields || {};
    const out: { label: string; value: string }[] = [];

    const pickKey = (candidates: string[]) =>
        Object.keys(ef).find((k) =>
            candidates.some((c) => k.toLowerCase().includes(c))
        );

    const ipKey = pickKey(["ip", "adresse_ip"]);
    if (ipKey && ef[ipKey]) out.push({ label: "IP", value: String(ef[ipKey]) });

    const debitKey = pickKey(["debit", "débit", "bandwidth", "bp"]);
    if (debitKey && ef[debitKey]) out.push({ label: "Débit", value: String(ef[debitKey]) });

    const typeKey = pickKey(["type_acces", "type_lien", "techno", "technologie"]);
    if (typeKey && ef[typeKey]) out.push({ label: "Type", value: String(ef[typeKey]) });

    return out;
};

/* ── composant ── */
const MesLiens = () => {
    const [services, setServices] = useState<ServiceItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const res: any = await axios.post("/apiV2/", { action: "services" }, apiV2PostConfig());
                const all: ServiceItem[] = res?.data ?? res ?? [];
                /* On ne garde que les liens internet (catégorie 7) */
                setServices(all.filter((x) => isLienCategory(x.service)));
            } catch {
                /* silencieux */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    /* ── KPI ── */
    const total = services.length;
    const actifs = services.filter((x) => x.service.statut_service === "Actif").length;
    const inactifs = services.filter((x) => x.service.statut_service === "Inactif").length;
    const autres = total - actifs - inactifs;

    const widgets = [
        { label: "Total liens", counter: total, icon: "ri-global-line", color: "primary" },
        { label: "Liens actifs", counter: actifs, icon: "ri-checkbox-circle-line", color: "success" },
        { label: "Liens inactifs", counter: inactifs, icon: "ri-close-circle-line", color: "danger" },
        { label: "En cours", counter: autres, icon: "ri-time-line", color: "warning" },
    ];

    /* ── filtre recherche ── */
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return services;
        return services.filter((x) => {
            const efValues = Object.values(x.service.extrafields || {})
                .map((v) => String(v ?? "").toLowerCase())
                .join(" ");
            return (
                x.contrat.ref.toLowerCase().includes(q) ||
                x.service.label.toLowerCase().includes(q) ||
                (x.service.ref_ext || "").toLowerCase().includes(q) ||
                efValues.includes(q)
            );
        });
    }, [services, search]);

    /* ── loading ── */
    if (loading) {
        return (
            <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="text-center">
                    <Spinner style={{ width: "3rem", height: "3rem", color: "#0066A1" }} />
                    <p className="text-muted mt-3 fw-medium">Chargement de vos liens…</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Mes Liens" pageTitle="Espace Client" />

                    {/* ── Widgets KPI ── */}
                    <Row>
                        {widgets.map((w, i) => (
                            <Col xxl={3} sm={6} key={i}>
                                <Card className="card-animate">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">{w.label}</p>
                                                <h2 className="mt-4 ff-secondary fw-semibold">
                                                    <CountUp start={0} end={w.counter} duration={2} />
                                                </h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className={`avatar-title rounded-circle fs-4 bg-${w.color}-subtle text-${w.color}`}>
                                                    <i className={w.icon}></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* ── Tableau ── */}
                    <Row>
                        <Col lg={12}>
                            <Card id="liensList">
                                <CardHeader className="border-0">
                                    <div className="d-flex align-items-center">
                                        <h5 className="card-title mb-0 flex-grow-1">Liste des liens internet</h5>
                                        <div className="flex-shrink-0">
                                            <div className="search-box">
                                                <Input
                                                    type="text"
                                                    className="form-control search"
                                                    placeholder="Rechercher un lien, contrat, IP…"
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    style={{ minWidth: 260 }}
                                                />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>

                                <CardBody className="pt-0">
                                    {filtered.length === 0 ? (
                                        <div className="text-center py-5">
                                            <div className="avatar-md mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3">
                                                    <i className="ri-global-line"></i>
                                                </span>
                                            </div>
                                            <h5 className="mt-2">Aucun lien trouvé</h5>
                                            <p className="text-muted">
                                                {search
                                                    ? "Aucun résultat pour cette recherche."
                                                    : "Vous n'avez pas encore de lien internet actif."}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive table-card mb-1">
                                            <table className="table align-middle table-nowrap mb-0">
                                                <thead className="table-light text-muted">
                                                    <tr>
                                                        <th>Contrat</th>
                                                        <th>Lien</th>
                                                        <th>Informations</th>
                                                        <th>Date ouverture</th>
                                                        <th className="text-center">Statut</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filtered.map((item, idx) => {
                                                        const isActif = item.service.statut_service === "Actif";
                                                        const date = isActif
                                                            ? item.service.date_ouverture
                                                            : item.service.date_ouverture_prevue;
                                                        const infos = getExtraInfo(item.service);
                                                        return (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <span className="fw-semibold link-primary">
                                                                        {item.contrat.ref}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="fw-medium">
                                                                        {item.service.label}
                                                                        {Number(item.service.qty ?? 1) > 1 && (
                                                                            <span className="text-muted ms-1">
                                                                                ({Number(item.service.qty)})
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {item.service.ref_ext && (
                                                                        <div className="text-muted fs-12">
                                                                            Réf. : {item.service.ref_ext}
                                                                        </div>
                                                                    )}
                                                                    {item.service.description && (
                                                                        <div
                                                                            className="text-muted fs-12 text-truncate"
                                                                            style={{ maxWidth: 360 }}
                                                                            dangerouslySetInnerHTML={{ __html: item.service.description }}
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {infos.length === 0 ? (
                                                                        <span className="text-muted">—</span>
                                                                    ) : (
                                                                        <div className="d-flex flex-wrap gap-1">
                                                                            {infos.map((it, j) => (
                                                                                <span
                                                                                    key={j}
                                                                                    className="badge bg-info-subtle text-info fs-12"
                                                                                >
                                                                                    <strong className="me-1">{it.label} :</strong>
                                                                                    {it.value}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="text-muted">{formatDate(date)}</td>
                                                                <td className="text-center">
                                                                    {isActif ? (
                                                                        <Badge className="bg-success-subtle text-success text-uppercase">
                                                                            <i className="ri-checkbox-circle-fill me-1"></i>Actif
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge className="bg-danger-subtle text-danger text-uppercase">
                                                                            <i className="ri-time-fill me-1"></i>
                                                                            {item.service.statut_service}
                                                                        </Badge>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default MesLiens;
